import { EventType } from './event';
import { DependentType } from './dependent';
import { UserType } from './user';
import * as segment from '@fullstory/proto/fs/api/segment';

// This file should only contain types and should not contain constants or functions

export type SearchType = EventType | UserType | DependentType;
export type SearchFilter = segment.UserFilter & segment.EventFilter & segment.DependentEventFilter;

export type ConstrainedSearchType<T extends SearchType> = T extends EventType
  ? 'event'
  : T extends DependentType
  ? 'dependent'
  : T extends UserType
  ? 'user'
  : never;

// Represents the union of "kind" properties in T.
export type Kind<T extends { kind: any }> = T['kind'];
// Represents the union of "subkind" properties for a given "kind" in T.
export type Subkind<T extends { kind: any; subkind: any }, K> = Extract<T, { kind: K }>['subkind'];
// Represents the union of "value" properties for a given "kind" and "subkind" in T.
export type Value<T extends { kind: any; subkind: any; value: any }, K, SK> = Extract<
  T,
  { kind: K; subkind: SK }
>['value'];

// Sentinel that represents unused/absent values in type definitions.
export type NONE_TYPE = 'none';

export enum UiOnlyKinds {
  // merges Defined Events and Custom Events into single kind
  orgEvent = 'orgEvent',
  // holds threshold options for an event
  threshold = 'threshold',
  // api error
  apiError = 'apiError',
}

// Client-specific query-rows that don't have an exact server-side representation but can be transformed into a
// tsexported equivalent subkind/value pairing.
export enum UiOnlySubkinds {
  // The following date-likes hold a single date value that gets later on converted to a range.
  isDateLike = 'isDateLike',
  // The following duration-likes hold a single value duration and later convert to a date range for server speak.
  lastDurationLike = 'lastDurationLike',
  notLastDurationLike = 'notLastDurationLike',
  nextDurationLike = 'nextDurationLike',
  notNextDurationLike = 'notNextDurationLike',
  // Subkinds for orgEvent, which merges Defined Events and Custom Events and Spec Events
  customAny = 'customAny',
  customNamed = 'customNamed',
  definedDefault = 'definedDefault',
  definedNamed = 'definedNamed',
  specObject = 'specObject',
  // Subkind for threshold
  thresholdIs = 'is',
}

export type NullType = {
  kind: NONE_TYPE;
  subkind: NONE_TYPE;
  value: NONE_TYPE;
};

export type UrlType<K extends string> =
  | UrlTypeNoAny<K>
  | {
      kind: K;
      subkind: segment.UrlOperator.operator_keys.any;
      value: NONE_TYPE;
    }
  | {
      kind: K;
      subkind: segment.UrlOperator.operator_keys.isSet;
      value: NONE_TYPE;
    }
  | {
      kind: K;
      subkind: segment.UrlOperator.operator_keys.isUnset;
      value: NONE_TYPE;
    };

type UrlSubkinds =
  | segment.UrlOperator.operator_keys.equals
  | segment.UrlOperator.operator_keys.notEquals
  | segment.UrlOperator.operator_keys.startsWith
  | segment.UrlOperator.operator_keys.endsWith
  | segment.UrlOperator.operator_keys.containsToken
  | segment.UrlOperator.operator_keys.notContainsToken
  | segment.UrlOperator.operator_keys.containsTokenStartsWith
  | segment.UrlOperator.operator_keys.substring
  | segment.UrlOperator.operator_keys.matches
  | segment.UrlOperator.operator_keys.notMatches;

export type UrlTypeNoAny<K extends string> = {
  kind: K;
  subkind: UrlSubkinds;
  value: UrlValue[];
};

type StringSubkinds =
  | segment.StringOperator.operator_keys.equals
  | segment.StringOperator.operator_keys.notEquals
  | segment.StringOperator.operator_keys.startsWith
  | segment.StringOperator.operator_keys.endsWith
  | segment.StringOperator.operator_keys.containsToken
  | segment.StringOperator.operator_keys.containsTokenStartsWith
  | segment.StringOperator.operator_keys.notContainsToken
  | segment.StringOperator.operator_keys.substring;

export type StringType<K extends string> =
  | {
      kind: K;
      subkind: StringSubkinds;
      value: (string | null)[];
    }
  | {
      kind: K;
      subkind: segment.StringOperator.operator_keys.any;
      value: NONE_TYPE;
    };

type DateSubkinds =
  | UiOnlySubkinds.isDateLike
  | segment.DateOperator.operator_keys.after
  | segment.DateOperator.operator_keys.before;

type DateRangeSubkinds =
  | segment.DateOperator.operator_keys.range
  | segment.DateOperator.operator_keys.notRange;

type DateDurationSubkinds = UiOnlySubkinds.lastDurationLike | UiOnlySubkinds.notLastDurationLike;

export type DateType<K extends string> =
  | {
      kind: K;
      subkind: DateSubkinds;
      value: Date | null;
    }
  | {
      kind: K;
      subkind: DateRangeSubkinds;
      value: DateRangeValueNullable;
    }
  | {
      kind: K;
      subkind: DateDurationSubkinds;
      value: Duration;
    };

type DurationSubkinds =
  | segment.DurationOperator.operator_keys.atLeast
  | segment.DurationOperator.operator_keys.atMost
  | segment.DurationOperator.operator_keys.lessThan
  | segment.DurationOperator.operator_keys.greaterThan;

type DurationRangeSubkinds =
  | segment.DurationOperator.operator_keys.range
  | segment.DurationOperator.operator_keys.notRange;

export type DurationType<K extends string> =
  | {
      kind: K;
      subkind: DurationSubkinds;
      value: Duration;
    }
  | {
      kind: K;
      subkind: DurationRangeSubkinds;
      value: [Duration, Duration];
    };

type StringRangeMultiValuedSubkinds =
  | segment.StringRangeOperator.operator_keys.equals
  | segment.StringRangeOperator.operator_keys.notEquals
  | segment.StringRangeOperator.operator_keys.startsWith
  | segment.StringRangeOperator.operator_keys.endsWith;

type StringRangeSingleValuedSubkinds =
  | segment.StringRangeOperator.operator_keys.lessThan
  | segment.StringRangeOperator.operator_keys.greaterThan
  | segment.StringRangeOperator.operator_keys.atMost
  | segment.StringRangeOperator.operator_keys.atLeast;

// RangeType represents search types for comparable numerical and
// string values.
export type StringRangeType<K extends string> =
  | {
      kind: K;
      subkind: StringRangeMultiValuedSubkinds;
      value: (string | null)[];
    }
  | {
      kind: K;
      subkind: StringRangeSingleValuedSubkinds;
      value: string | null;
    };

type NumericMultiValuedSubkinds =
  | segment.NumericOperator.operator_keys.equals
  | segment.NumericOperator.operator_keys.notEquals;

type NumericSingleValuedSubkinds =
  | segment.NumericOperator.operator_keys.lessThan
  | segment.NumericOperator.operator_keys.greaterThan
  | segment.NumericOperator.operator_keys.atMost
  | segment.NumericOperator.operator_keys.atLeast;

type NumericRangeSubkinds =
  | segment.NumericOperator.operator_keys.range
  | segment.NumericOperator.operator_keys.notRange;

export type NumericSubkinds =
  | NumericMultiValuedSubkinds
  | NumericSingleValuedSubkinds
  | NumericRangeSubkinds;

// RangeType represents search types for comparable numerical and
// string values.
export type NumericRangeType<K extends string> =
  | {
      kind: K;
      subkind: NumericMultiValuedSubkinds;
      value: (number | null)[];
    }
  | {
      kind: K;
      subkind: NumericSingleValuedSubkinds;
      value: number | null;
    }
  | {
      kind: K;
      subkind: NumericRangeSubkinds;
      value: [number | null, number | null];
    };

type NumericIdSubkinds =
  | segment.NumericIdOperator.operator_keys.equals
  | segment.NumericIdOperator.operator_keys.notEquals;

// NumericIdType represents search types for comparable numerical id values.
export type NumericIdType<K extends string> =
  | {
      kind: K;
      subkind: segment.NumericIdOperator.operator_keys.any;
      value: NONE_TYPE;
    }
  | {
      kind: K;
      subkind: NumericIdSubkinds;
      value: (number | null)[];
    };

export type NumericIdTypeEquals<K extends string> = {
  kind: K;
  subkind: segment.NumericIdOperator.operator_keys.equals;
  value: (number | null)[];
};

type PageRefSubkinds =
  | segment.PageRefOperator.operator_keys.equals
  | segment.PageRefOperator.operator_keys.notEquals;

export enum PageRefValueType {
  Id = 'id',
  Spec = 'spec',
}

export type PageRefValueId = {
  type: PageRefValueType.Id;
  value: number;
};

export type PageRefValueSpec = {
  type: PageRefValueType.Spec;
  value: string;
};

export type PageRefValue = PageRefValueId | PageRefValueSpec;

export type PageRefType<K extends string> =
  | {
      kind: K;
      subkind: segment.PageRefOperator.operator_keys.any;
      value: NONE_TYPE;
    }
  | {
      kind: K;
      subkind: PageRefSubkinds;
      value: (PageRefValue | null)[];
    };

export type PageRefTypeEquals<K extends string> = {
  kind: K;
  subkind: segment.PageRefOperator.operator_keys.equals;
  value: (PageRefValue | null)[];
};

type FloatSubkinds =
  | segment.FloatOperator.operator_keys.equals
  | segment.FloatOperator.operator_keys.notEquals
  | segment.FloatOperator.operator_keys.atLeast
  | segment.FloatOperator.operator_keys.atMost
  | segment.FloatOperator.operator_keys.lessThan
  | segment.FloatOperator.operator_keys.greaterThan;

type FloatRangeSubkinds =
  | segment.FloatOperator.operator_keys.range
  | segment.FloatOperator.operator_keys.notRange;

// FloatType represents search types for comparable floating-point values.
export type FloatType<K extends string> =
  | {
      kind: K;
      subkind: FloatSubkinds;
      value: number | null;
    }
  | {
      kind: K;
      subkind: FloatRangeSubkinds;
      value: [number | null, number | null];
    };

export enum ElementValueType {
  Id = 'Id',
  Spec = 'Spec',
}

export type ElementValue = {
  type: ElementValueType;
  value: string;
};

type ElementStringSubkinds =
  | segment.ElementOperator.operator_keys.text
  | segment.ElementOperator.operator_keys.textIsNot
  | segment.ElementOperator.operator_keys.textStartsWith
  | segment.ElementOperator.operator_keys.textEndsWith
  | segment.ElementOperator.operator_keys.textContainsToken
  | segment.ElementOperator.operator_keys.textNotContainsToken
  | segment.ElementOperator.operator_keys.textContainsTokenStartsWith
  | segment.ElementOperator.operator_keys.css
  | segment.ElementOperator.operator_keys.cssIsNot
  | segment.ElementOperator.operator_keys.exactCss
  | segment.ElementOperator.operator_keys.cssSubstring
  | segment.ElementOperator.operator_keys.withHistoricalElementId
  | segment.ElementOperator.operator_keys.withoutHistoricalElementId;

// ElementType represents search types for DOM element properties.
export type ElementType<K extends string> =
  | {
      kind: K;
      subkind: segment.ElementOperator.operator_keys.any;
      value: NONE_TYPE;
    }
  | ElementStringType<K>
  | {
      kind: K;
      subkind: segment.ElementOperator.operator_keys.withSetHistoricalElementId;
      value: NONE_TYPE;
    }
  | {
      kind: K;
      subkind: segment.ElementOperator.operator_keys.withUnsetHistoricalElementId;
      value: NONE_TYPE;
    }
  | ElementValueIdType<K>;

export type ElementValueIdType<K extends string> = {
  kind: K;
  subkind: segment.ElementOperator.operator_keys.withElementId;
  value: (ElementValue | null)[];
};

export type ElementStringType<K extends string> = {
  kind: K;
  subkind: ElementStringSubkinds;
  value: (string | null)[];
};

type BoolSubkinds =
  | segment.BoolOperator.operator_keys.equals
  | segment.BoolOperator.operator_keys.notEquals;

export type BoolType<K extends string> = {
  kind: K;
  subkind: BoolSubkinds;
  value: boolean;
};

export enum CustomFieldType {
  Name = 'name',
  Spec = 'spec',
}

export type CustomFieldName = {
  type: CustomFieldType.Name;
  name: string;
};

export type CustomFieldSpec = {
  type: CustomFieldType.Spec;
  slug: string;
};

export type CustomFieldIdent = CustomFieldName | CustomFieldSpec;

type CustomFieldIsSetSubkinds = 'isSet' | 'isUnset';

type CustomFieldStringSubkinds =
  | segment.CustomFieldStringOperator.operator_keys.equals
  | segment.CustomFieldStringOperator.operator_keys.notEquals
  | segment.CustomFieldStringOperator.operator_keys.startsWith
  | segment.CustomFieldStringOperator.operator_keys.endsWith
  | segment.CustomFieldStringOperator.operator_keys.substring;

// Custom field types are the higher order types for user vars and
// custom event properties.
export type CustomFieldStringType<
  K extends
    | segment.UserFilter.filter_keys.userVarString
    | segment.DependentEventFilter.filter_keys.pageVarString
    | segment.DependentEventFilter.filter_keys.userVarString
    | segment.DependentEventFilter.filter_keys.customEventPropertyString
    | segment.DependentEventFilter.filter_keys.elemVarString
    | segment.DependentEventFilter.filter_keys.fsevtVarString,
> =
  | {
      kind: K;
      subkind: CustomFieldStringSubkinds;
      value: (string | null)[];
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldIsSetSubkinds;
      value: NONE_TYPE;
      field: CustomFieldIdent;
    };

type CustomFieldStringsSubkinds =
  | segment.CustomFieldStringsOperator.operator_keys.equals
  | segment.CustomFieldStringsOperator.operator_keys.notEquals
  | segment.CustomFieldStringsOperator.operator_keys.startsWith
  | segment.CustomFieldStringsOperator.operator_keys.endsWith
  | segment.CustomFieldStringsOperator.operator_keys.substring;

export type CustomFieldStringsType<
  K extends
    | segment.UserFilter.filter_keys.userVarStrings
    | segment.DependentEventFilter.filter_keys.pageVarStrings
    | segment.DependentEventFilter.filter_keys.userVarStrings
    | segment.DependentEventFilter.filter_keys.customEventPropertyStrings
    | segment.DependentEventFilter.filter_keys.elemVarStrings
    | segment.DependentEventFilter.filter_keys.fsevtVarStrings,
> =
  | {
      kind: K;
      subkind: CustomFieldStringsSubkinds;
      value: (string | null)[];
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldIsSetSubkinds;
      value: NONE_TYPE;
      field: CustomFieldIdent;
    };

type CustomFieldDateSubkinds =
  | segment.CustomFieldDateOperator.operator_keys.after
  | segment.CustomFieldDateOperator.operator_keys.before
  | UiOnlySubkinds.isDateLike;

type CustomFieldDateRangeSubkinds =
  | segment.CustomFieldDateOperator.operator_keys.range
  | segment.CustomFieldDateOperator.operator_keys.notRange;

type CustomFieldDateDurationSubkinds =
  | UiOnlySubkinds.lastDurationLike
  | UiOnlySubkinds.notLastDurationLike
  | UiOnlySubkinds.nextDurationLike
  | UiOnlySubkinds.notNextDurationLike;

export type CustomFieldDateType<
  K extends
    | segment.DependentEventFilter.filter_keys.pageVarDate
    | segment.DependentEventFilter.filter_keys.pageVarDates
    | segment.UserFilter.filter_keys.userVarDate
    | segment.UserFilter.filter_keys.userVarDates
    | segment.DependentEventFilter.filter_keys.userVarDate
    | segment.DependentEventFilter.filter_keys.userVarDates
    | segment.DependentEventFilter.filter_keys.customEventPropertyDates
    | segment.DependentEventFilter.filter_keys.customEventPropertyDate
    | segment.DependentEventFilter.filter_keys.elemVarDate
    | segment.DependentEventFilter.filter_keys.elemVarDates
    | segment.DependentEventFilter.filter_keys.fsevtVarDate
    | segment.DependentEventFilter.filter_keys.fsevtVarDates,
> =
  | {
      kind: K;
      subkind: CustomFieldDateSubkinds;
      value: Date | null;
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldDateRangeSubkinds;
      value: [Date | null, Date | null];
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldDateDurationSubkinds;
      value: Duration;
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldIsSetSubkinds;
      value: NONE_TYPE;
      field: CustomFieldIdent;
    };

type CustomFieldCountMultiValuedSubkinds = 'equals' | 'notEquals';

type CustomFieldCountSingleValuedSubkinds = 'lessThan' | 'greaterThan' | 'atMost' | 'atLeast';

type CustomFieldCountRangeSubkinds = 'range' | 'notRange';

export type CustomFieldCountType<
  K extends
    | segment.DependentEventFilter.filter_keys.pageVarInt
    | segment.DependentEventFilter.filter_keys.pageVarFloat
    | segment.UserFilter.filter_keys.userVarInt
    | segment.UserFilter.filter_keys.userVarFloat
    | segment.DependentEventFilter.filter_keys.userVarInt
    | segment.DependentEventFilter.filter_keys.userVarFloat
    | segment.DependentEventFilter.filter_keys.customEventPropertyInt
    | segment.DependentEventFilter.filter_keys.customEventPropertyFloat
    | segment.DependentEventFilter.filter_keys.elemVarInt
    | segment.DependentEventFilter.filter_keys.elemVarFloat
    | segment.DependentEventFilter.filter_keys.fsevtVarInt
    | segment.DependentEventFilter.filter_keys.fsevtVarFloat,
> =
  | {
      kind: K;
      subkind: CustomFieldCountMultiValuedSubkinds;
      value: (number | null)[];
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldCountSingleValuedSubkinds;
      value: number | null;
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldCountRangeSubkinds;
      value: [number | null, number | null];
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldIsSetSubkinds;
      value: NONE_TYPE;
      field: CustomFieldIdent;
    };

type CustomFieldCountsMultiValuedSubkinds = 'equals' | 'notEquals';

type CustomFieldCountsSingleValuedSubkinds = 'lessThan' | 'greaterThan' | 'atMost' | 'atLeast';

type CustomFieldCountsRangeSubkinds = 'range' | 'notRange';

export type CustomFieldCountsType<
  K extends
    | segment.DependentEventFilter.filter_keys.pageVarInts
    | segment.DependentEventFilter.filter_keys.pageVarFloats
    | segment.UserFilter.filter_keys.userVarInts
    | segment.UserFilter.filter_keys.userVarFloats
    | segment.DependentEventFilter.filter_keys.userVarInts
    | segment.DependentEventFilter.filter_keys.userVarFloats
    | segment.DependentEventFilter.filter_keys.customEventPropertyInts
    | segment.DependentEventFilter.filter_keys.customEventPropertyFloats
    | segment.DependentEventFilter.filter_keys.elemVarInts
    | segment.DependentEventFilter.filter_keys.elemVarFloats
    | segment.DependentEventFilter.filter_keys.fsevtVarInts
    | segment.DependentEventFilter.filter_keys.fsevtVarFloats,
> =
  | {
      kind: K;
      subkind: CustomFieldCountsMultiValuedSubkinds;
      value: (number | null)[];
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldCountsSingleValuedSubkinds;
      value: number | null;
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldCountsRangeSubkinds;
      value: [number | null, number | null];
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldIsSetSubkinds;
      value: NONE_TYPE;
      field: CustomFieldIdent;
    };

type CustomFieldBoolSubkinds =
  | segment.CustomFieldBoolOperator.operator_keys.equals
  | segment.CustomFieldBoolOperator.operator_keys.notEquals;

export type CustomFieldBoolType<
  K extends
    | segment.DependentEventFilter.filter_keys.pageVarBool
    | segment.DependentEventFilter.filter_keys.pageVarBools
    | segment.UserFilter.filter_keys.userVarBool
    | segment.UserFilter.filter_keys.userVarBools
    | segment.DependentEventFilter.filter_keys.userVarBool
    | segment.DependentEventFilter.filter_keys.userVarBools
    | segment.DependentEventFilter.filter_keys.customEventPropertyBools
    | segment.DependentEventFilter.filter_keys.customEventPropertyBool
    | segment.DependentEventFilter.filter_keys.elemVarBool
    | segment.DependentEventFilter.filter_keys.elemVarBools
    | segment.DependentEventFilter.filter_keys.fsevtVarBool
    | segment.DependentEventFilter.filter_keys.fsevtVarBools,
> =
  | {
      kind: K;
      subkind: CustomFieldBoolSubkinds;
      value: boolean;
      field: CustomFieldIdent;
    }
  | {
      kind: K;
      subkind: CustomFieldIsSetSubkinds;
      value: NONE_TYPE;
      field: CustomFieldIdent;
    };

export type NoneType<K extends string> = {
  kind: K;
  subkind: NONE_TYPE;
  value: NONE_TYPE;
};

// null represents unselected
export enum UrlCategory {
  full = 'full',
  host = 'host',
  path = 'path',
  new_path = 'new_path',
  query = 'query',
  fragment = 'fragment',
}

export type UrlValue = {
  value: string | null;
  category: UrlCategory | null;
};

export enum DurationUnit {
  milli = 'milli',
  second = 'second',
  minute = 'minute',
  hour = 'hour',
  day = 'day',
}

// DurationScalarMs gives the millisecond value of each base unit.
export const durationScalarMs: Map<DurationUnit, number> = new Map([
  [DurationUnit.milli, 1],
  [DurationUnit.second, 1 * 1000],
  [DurationUnit.minute, 1 * 1000 * 60],
  [DurationUnit.hour, 1 * 1000 * 60 * 60],
  [DurationUnit.day, 1 * 1000 * 60 * 60 * 24],
]);

export enum SolrUnit {
  milli = 'MILLI',
  second = 'SECOND',
  minute = 'MINUTE',
  hour = 'HOUR',
  day = 'DAY',
}

type DurationScalar = number | null;
export const EmptyDurationValue: DurationScalar = null;

export type Duration = {
  unit: DurationUnit;
  scalar: DurationScalar;
};

export const PartialDuration: Duration = {
  unit: DurationUnit.day,
  scalar: EmptyDurationValue,
};

export type DateRangeValue = [Date, Date];
export type DateRangeValueNullable = [Date | null, Date | null];

export enum Device {
  any = 'Any',
  desktop = 'Desktop',
  mobile = 'Mobile',
  tablet = 'Tablet',
  robot = 'Robot',
  unknown = 'Unknown',
}

export enum OS {
  any = 'Any',
  windows = 'Windows',
  osx = 'OS X',
  chrome = 'Chrome OS',
  android = 'Android',
  ios = 'iOS',
  linux = 'Linux',
  windowsphone = 'Windows Phone',
  robot = 'Robot',
  unknown = 'Unknown',
}

export enum Browser {
  any = 'Any',
  chrome = 'Chrome',
  ie = 'Internet Explorer',
  edge = 'Microsoft Edge',
  firefox = 'Firefox',
  safari = 'Safari',
  opera = 'Opera',
  robot = 'Robot',
  yandex = 'Yandex',
  mobileapp = 'Mobile App',
  coccoc = 'Coc Coc',
  customagent = 'Custom Agent',
  custommobileagent = 'Custom Mobile Agent',
  duckduckgo = 'DuckDuckGo',
  electron = 'Electron',
  lynx = 'Lynx',
  mozillaother = 'Mozilla (Other)',
  seamonkey = 'SeaMonkey',
  ucweb = 'UC Browser',
  unknown = 'Unknown',
}

export enum Platform {
  any = 'Any',
  web = 'Web',
  nativemobile = 'Mobile Apps',
  server = 'Server',
}

export enum PinchGesture {
  in = 'Zoom In',
  out = 'Zoom Out',
}

export enum SeenVisibility {
  InViewport = 'in viewport',
  NotInViewport = 'not in viewport',
}

export enum ErrorClickType {
  any = 'Any',
  console = 'Console Error',
  exception = 'Uncaught Exception',
}

export enum DatalossType {
  paymentcard = 'Payment Card',
  email = 'Email',
  ssn = 'Social Security',
  password = 'Password',
}

export enum SessionType {
  any = 'Any',
  live = 'Live',
  preview = 'Preview',
}
